import React, { useState } from 'react';
import { ChevronLeft, Lock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { MockProfile } from '../data/mockProfiles';
import { TOPICS } from '../data/topics';
import { useMeetingRequests } from './MeetingRequestsContext';
interface Props {
  profile: MockProfile;
  onBack: () => void;
}
export function ProfileDetail({
  profile,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const {
    sendRequest
  } = useMeetingRequests();
  const [sent, setSent] = useState(false);
  const profileTopics = TOPICS.filter(tp => profile.topicIds.includes(tp.id));
  const handleSendRequest = () => {
    // Use a default slot for the connection request
    sendRequest(profile.id, 'may8-am');
    setSent(true);
  };
  if (sent) {
    return <div className="flex flex-col h-full w-full bg-[#0D0D0D] animate-in fade-in duration-300" data-id="element-175">
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-176">
          {/* Blurred avatar with check overlay */}
          <div className="relative mb-6" data-id="element-177">
            <div className="w-20 h-20 rounded-full bg-[#2A2A2A] overflow-hidden relative" data-id="element-178">
              <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
              filter: 'blur(4px)'
            }} data-id="element-179" />
              <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-180" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#C5D82D] flex items-center justify-center shadow-lg" data-id="element-181">
              <CheckCircle2 className="w-5 h-5 text-[#0D0D0D]" strokeWidth={2.5} data-id="element-182" />
            </div>
          </div>

          <h2 className="text-[22px] text-white font-semibold mb-1" data-id="element-183">
            Richiesta inviata!
          </h2>
          <p className="text-[13px] text-[#A0A0A0] mb-1" data-id="element-184">
            {profile.role} · {profile.sector}
          </p>

          {/* Info card */}
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-[14px] p-4 mt-6 w-full max-w-[320px] flex items-start gap-3" data-id="element-185">
            <Lock className="w-4 h-4 text-[#C5D82D] shrink-0 mt-0.5" strokeWidth={2} data-id="element-186" />
            <p className="text-[13px] text-[#A0A0A0] leading-relaxed text-left" data-id="element-187">
              Monitora la risposta nel tab{' '}
              <span className="text-white font-medium" data-id="element-188">Connessioni</span>.
              Riceverai una notifica quando il profilo risponde.
            </p>
          </div>
        </div>

        {/* Bottom button */}
        <div className="shrink-0 px-6 pt-4 pb-24" data-id="element-189">
          <button onClick={onBack} className="w-full py-4 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold tracking-wide active:scale-[0.98] transition-transform" data-id="element-190">
            Torna indietro
          </button>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative animate-in fade-in duration-200" data-id="element-191">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 pt-5 pb-8" data-id="element-192">
        <button onClick={onBack} aria-label={t('common.back')} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full active:bg-[#141414] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D]" data-id="element-193">
          <ChevronLeft className="w-6 h-6 text-white" strokeWidth={2} data-id="element-194" />
        </button>

        {/* Blurred identity header */}
        <div className="flex flex-col items-center text-center mt-2 mb-8" data-id="element-195">
          <div className="w-24 h-24 rounded-full bg-[#2A2A2A] shrink-0 overflow-hidden relative mb-4" data-id="element-196">
            <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
            filter: 'blur(4px)'
          }} data-id="element-197" />
            <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-198" />
          </div>
          <h1 className="text-[18px] text-white font-semibold" data-id="element-199">
            {profile.role}
          </h1>
          <p className="text-[13px] text-[#A0A0A0] mt-1" data-id="element-200">{profile.sector}</p>
        </div>

        {/* Topics of interest */}
        <h2 className="text-[13px] uppercase tracking-wide text-[#6A6A6A] font-semibold mb-3" data-id="element-201">
          {t('profile.topics')}
        </h2>
        <div className="flex flex-wrap gap-2 mb-8" data-id="element-202">
          {profileTopics.map(tp => <span key={tp.id} className="px-3 py-1.5 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] text-white text-[12px]" data-id="element-203">
              {t(tp.nameKey)}
            </span>)}
        </div>

        {/* Reveal notice */}
        <div className="bg-[#141414] border border-[#2A2A2A] rounded-[14px] p-4 flex items-start gap-3" data-id="element-204">
          <Lock className="w-4 h-4 text-[#C5D82D] shrink-0 mt-0.5" strokeWidth={2} data-id="element-205" />
          <p className="text-[13px] text-[#A0A0A0] leading-relaxed" data-id="element-206">
            {t('profile.blurNotice')}
          </p>
        </div>
      </div>

      {/* Sticky footer CTA */}
      <div className="shrink-0 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-[#1A1A1A] px-6 pt-4 pb-24 z-30" data-id="element-207">
        <button onClick={handleSendRequest} className="w-full py-4 rounded-full text-[14px] font-semibold tracking-wide bg-[#C5D82D] text-[#0D0D0D] active:scale-[0.98] transition-transform" data-id="element-208">
          Invia richiesta di connessione
        </button>
      </div>
    </div>;
}